/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, computed, flow, toJS } from 'mobx';
import get from 'lodash/get';
import map from 'lodash/map';
import last from 'lodash/last';
import fetch from '../fetch';
import { isError, generateUpdateSingle } from './actionCreator';
import _ from '../translations';
import moment from 'moment';
import { clearStores } from '../App.js';
import ErrorsStore from './errorsStore';
import { captureExceptionSentry, setBreadcrumbSentry, setSentryUser } from '../utils/sentry';

class ProfileStore {
    @observable
    isLoggedIn = false;
    @observable
    isOrderExpired = false;
    @observable
    profileIsLoading = false;
    @observable
    profile = false;
    @observable
    role = false;
    @observable
    session = false;
    @observable
    loginError = false;
    @observable
    isRoot = false;
    @observable
    client = {};
    @observable
    clientsList = [];
    @observable
    customer = false;
    @observable
    lastOrder = false;

    constructor(domain = '') {
        this.domain = domain;
        this.baseUrl = `${domain}/api/auth/users/profile`;
        const client = localStorage.getItem('_nunchee_console_client');
        if (client) this.client = JSON.parse(client);
    }

    @action
    reset = () => {
        this.isLoggedIn = false;
        this.isOrderExpired = false;
        this.profileIsLoading = false;
        this.profile = false;
        this.session = false;
        this.loginError = false;
        this.isRoot = false;
        this.client = {};
        this.clientsList = [];
        localStorage.removeItem('_nunchee_console_client');
    };

    @action.bound
    async getClients(filters) {
        const params = {
            fields: 'name,profile,isNuncheeBasic,isGoku,settings.plugins',
            paginate: 'false',
            ...filters,
        };

        const clients = await fetch.get({
            url: `${this.domain}/api/core/clients`,
            params,
            disableClientFilter: true,
        });

        if (clients.error)
            return [];

        const listClient = get(clients, 'items', []);

        return listClient;
    };

    getBasicClients() {
        const filters = {
            'filterBy-isNuncheeBasic': true,
        }
        return this.getClients(filters);
    };

    getGokuClient() {
        const filters = {
            'filterBy-isGoku': true,
        }
        return this.getClients(filters);
    };

    @computed
    get clientsForSelect() {
        return map(toJS(this.clientsList), client => {
            return {
                text: client.name,
                value: client._id,
                key: client._id,
            };
        });
    }

    @action.bound
    loadProfile = flow(function*() {
        this.profileIsLoading = true;
        try {
            const profile = yield fetch.get({ url: `${this.domain}/api/auth/users/profile` });

            if (profile.error || profile?.statusCode === 500) {
                this.profileIsLoading = false;
                return;
            }

            this.loginError = false;
            this.isLoggedIn = true;
            this.profile = profile;

            this.session = yield fetch.get({ url: `${this.domain}/api/auth/users/me` });
            this.role = get(this.session, 'user.rbac.role', '');
            const listClients = [];

            setSentryUser({ 
                id: this.session?.user?._id,
                username: profile?.email,
                role: this.role,
            });

            if (this.role === 'ROOT') {
                const gokuClient =  yield this.getGokuClient();
                const basicClients = yield this.getBasicClients();

                listClients.push(...gokuClient, ...basicClients);
                this.clientsList = listClients;
                this.isRoot = true;
            } else if (this.role === 'Admin') {
                const basicClients = yield this.getBasicClients();
                const orders = yield fetch.get({ url: `${this.domain}/api/commerce/orders/listAdminOrders` });
                const lastOrder = last(get(orders, 'items', []));

                this.clientsList = basicClients;
                this.client = toJS(basicClients[0]);

                const customerId = get(this.client, 'profile.customer_id');

                if (get(lastOrder, 'status') === 4) {
                    const customer = yield fetch.get({
                        url: `${this.domain}/api/stripe/admin/getCustomer/${customerId}`,
                        params: {},
                    });
                    this.lastOrder = lastOrder;
                    this.isOrderExpired = true;
                    this.customer = customer;
                }
                if (!this.profile.firstLogin) this.setFirstLogin();
            }
            this.profileIsLoading = false;
        } catch (e) {
            console.error(e);
            this.profileIsLoading = false;
        }
    });

    @action.bound
    login = flow(function*(body = {}) {
        const { username, password } = get(body, 'user', {});

        if (!username || !password) {
             this.loginError = {
                userMessage: {
                    original: _('Username or password is not valid'),
                },
            };
            return;
        }
        this.profileIsLoading = true;
        try {
            const response = yield fetch.post({
                url: `${this.domain}/api/auth/login/admin/`, 
                body,
            });

            if (isError(response)) {
                this.loginError = response.error;
                this.profileIsLoading = false;
                return;
            }

            const profile = yield fetch.get({ url: `${this.domain}/api/auth/users/profile` });

            if (profile.error) {
                this.profileIsLoading = false;
                return;
            }

            this.profile = profile;
            this.loginError = false;
            this.isLoggedIn = true;
            this.session = response;
            this.role = get(response, 'user.rbac.role', '');
            const listClients = [];

            setSentryUser({ 
                id: response?.user?._id,
                username,
                role: this.role,
            });

            setBreadcrumbSentry({ 
                type: 'info',
                category: 'log',
                message : "Login",
                data: {                          
                    section: '/login',
                    action: 'profileStore.login'
                }
            });

            if (this.role === 'ROOT') {
                const gokuClient =  yield this.getGokuClient();
                const basicClients = yield this.getBasicClients();

                listClients.push(...gokuClient, ...basicClients);
                this.clientsList = listClients;
                this.isRoot = true;

            } else if (this.role === 'Admin') {
                const basicClients = yield this.getBasicClients();
                const orders = yield fetch.get({ url: `${this.domain}/api/commerce/orders/listAdminOrders` });
                const lastOrder = last(get(orders, 'items', []));

                this.clientsList = basicClients;
                this.client = toJS(basicClients[0]);

                const customerId = get(this.client, 'profile.customer_id');

                if (get(lastOrder, 'status') === 4) {
                    const customer = yield fetch.get({
                        url: `${this.domain}/api/stripe/admin/getCustomer/${customerId}`,
                        params: {},
                    });
                    this.lastOrder = lastOrder;
                    this.isOrderExpired = true;
                    this.customer = customer;
                }

                if (!this.profile.firstLogin) this.setFirstLogin();

            } else {
                this.loginError = {
                    userMessage: {
                        original: _('You must be Admin to log in'),
                    },
                };
            }
            this.profileIsLoading = false;
        } catch (e) {
            captureExceptionSentry(e);
            console.warn(e);
        }
    });

    @action.bound
    setClient(client) {
        if (!client) return;

        this.client = toJS(client);
        localStorage.setItem('_nunchee_console_client', JSON.stringify(client));
    }

    @action.bound
    logout = flow(function*() {
        try {
            this.profileIsLoading = true;
            yield fetch.post({
                url: `${this.domain}/api/auth/users/admin/logout`,
            });
            let session = fetch.get({ url: `${this.domain}/api/auth/users/me` });
            let profile = fetch.get({ url: `${this.domain}/api/auth/users/profile` });
            const result = [yield session, yield profile];
            session = result[0];
            profile = result[1];
            this.profileIsLoading = false;
            this.session = session;
            this.profile = false;
            this.isLoggedIn = false;
            this.client = false;
            this.isRoot = false;

            if(window.Intercom){
                window.Intercom('shutdown');
            }
            
            setBreadcrumbSentry({ 
                type: 'info',
                category: 'log',
                message : "Login",
                data: {                          
                    section: '/logout',
                    action: 'profileStore.logout'
                }
            });

            setSentryUser(null);

            localStorage.removeItem('_nunchee_console_client');
        } catch (e) {
            captureExceptionSentry(e);
            console.warn(e);
        } finally {
            clearStores();
        }
    });

    /*There is no need this to be an action, because we don't want to update the profile data right now*/
    async setFirstLogin() {
        const now = moment()
            .utc()
            .format();
        let data = toJS(this.profile);
        data.firstLogin = now;
        await this.updateProfile({ data });
    }

    recoverPassword = flow(function*(email) {
        try {
            const result = yield fetch.post({
                url: `${this.domain}/api/auth/users/password/recover`,
                body: { data: { email } },
            });
            if (isError(result)) {
                ErrorsStore.addError(
                    result,
                    'There was an error in your password recover request. Please try again later.'
                );
            }
            return result;
        } catch (e) {
            ErrorsStore.addError(e, 'There was an error in your password recover request. Please try again later.');
            console.warn(e);
            return false;
        }
    });

    changePassword = flow(function*(hash, newPassword) {
        try {
            const result = yield fetch.post({
                url: `${this.domain}/api/auth/users/password/recover/change`,
                body: { data: { hash, newPassword } },
            });
            if (isError(result)) {
                ErrorsStore.addError(
                    result,
                    'There was an error in your password recover request. Please try again later.'
                );
            }
            return result;
        } catch (e) {
            ErrorsStore.addError(e, 'There was an error in your password recover request. Please try again later.');
            console.warn(e);
            return false;
        }
    });

    /*Just for testing*/
    async _deleteFirstLogin() {
        let data = toJS(this.profile);
        data.firstLogin = false;
        await this.updateProfile({ data });
    }

    @action.bound
    updateProfile = flow(function*({ data } = {}) {
        if (!data) return;
        return yield generateUpdateSingle({
            data,
            url: `${this.domain}/api/auth/users/updateProfile`,
            store: this,
            customError: _('There was a problem updating profile'),
        })();
    });
}

//export class for tests
export { ProfileStore };
//export singleton for app
const profileStore = new ProfileStore();
export default profileStore;
